import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  ShieldAlert, Settings as SettingsIcon, ListFilter, LogOut, Gift,
  Menu, X, BookOpen, Tag, LogIn, BellRing,
} from "lucide-react";
import { tokens } from "@/lib/api";
import { SubscriptionBanner } from "@/components/SubscriptionBanner";

const SIGNED_IN_NAV = [
  { to: "/alerts", label: "Recalls", icon: ListFilter, testid: "feed" },
  { to: "/blog", label: "Blog", icon: BookOpen, testid: "blog" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "settings" },
  { to: "/referrals", label: "Refer a friend", icon: Gift, testid: "referrals" },
];

const SIGNED_OUT_NAV = [
  { to: "/alerts", label: "Recall history", icon: ListFilter, testid: "recalls" },
  { to: "/blog", label: "Blog", icon: BookOpen, testid: "blog" },
  { to: "/about", label: "Our story", icon: BookOpen, testid: "about" },
  { to: "/pricing", label: "Pricing", icon: Tag, testid: "pricing" },
  { to: "/login", label: "Login", icon: LogIn, testid: "login" },
];

export const Brand = ({ compact }) => (
  <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
    <span className="flex h-8 w-8 items-center justify-center rounded-md bg-red-700 text-white">
      <ShieldAlert className="h-4 w-4" />
    </span>
    <span className="whitespace-nowrap font-display font-semibold tracking-tight text-slate-900">
      <span className="hidden sm:inline">Food Recall Alerts</span>
      <span className="sm:hidden">Recall Alerts</span>
      {!compact && <span className="hidden sm:inline text-slate-400 font-normal"> / SMS alerts</span>}
    </span>
  </Link>
);

export const AppHeader = () => {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const signedIn = !!tokens.get();
  const [open, setOpen] = useState(false);
  const items = signedIn ? SIGNED_IN_NAV : SIGNED_OUT_NAV;

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const signOut = () => { tokens.clear(); setOpen(false); nav("/"); };

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur-xl">
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-5 py-3.5">
        <Brand />

        <nav className="hidden items-center gap-1 md:flex">
          {items.map(({ to, label, icon: Icon, testid }) => (
            <Link
              key={to}
              to={to}
              data-testid={`nav-${testid}`}
              className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors hover:bg-slate-100 ${pathname === to ? "bg-slate-100 text-slate-900" : "text-slate-500"}`}
            >
              <Icon className="h-4 w-4" /> {signedIn ? label.split(" ")[0] : label}
            </Link>
          ))}
          {signedIn ? (
            <button
              data-testid="nav-signout"
              onClick={signOut}
              aria-label="Sign out"
              className="flex items-center rounded-full px-4 py-2 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100"
            >
              <LogOut className="h-4 w-4" />
            </button>
          ) : (
            <Link to="/join" data-testid="nav-join" className="rounded-full bg-slate-900 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800">
              Get alerts
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-2 md:hidden">
          {!signedIn && (
            <Link to="/join" data-testid="nav-join-mobile" className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white">
              Get alerts
            </Link>
          )}
          <button
            data-testid="nav-hamburger"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 text-slate-700 transition-colors active:bg-slate-100"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <>
          <button
            aria-hidden="true"
            tabIndex={-1}
            onClick={() => setOpen(false)}
            className="fixed inset-0 top-[65px] z-30 cursor-default bg-slate-900/20 md:hidden"
          />
          <nav
            data-testid="mobile-menu"
            className="relative z-40 border-t border-slate-200 bg-white px-5 pb-5 pt-3 shadow-lg md:hidden"
          >
            {items.map(({ to, label, icon: Icon, testid }) => (
              <Link
                key={to}
                to={to}
                data-testid={`mobile-nav-${testid}`}
                className={`flex items-center gap-3 rounded-xl px-3 py-3.5 text-base font-medium transition-colors active:bg-slate-100 ${pathname === to ? "bg-slate-100 text-slate-900" : "text-slate-700"}`}
              >
                <Icon className="h-5 w-5 text-slate-400" /> {label}
              </Link>
            ))}
            {signedIn ? (
              <button
                data-testid="mobile-nav-signout"
                onClick={signOut}
                className="mt-1 flex w-full items-center gap-3 rounded-xl px-3 py-3.5 text-base font-medium text-slate-700 transition-colors active:bg-slate-100"
              >
                <LogOut className="h-5 w-5 text-slate-400" /> Sign out
              </button>
            ) : (
              <Link
                to="/join"
                data-testid="mobile-nav-join"
                className="mt-2 flex items-center justify-center gap-2 rounded-full bg-red-700 px-5 py-3.5 text-base font-semibold text-white"
              >
                <BellRing className="h-4 w-4" /> Turn on SMS alerts
              </Link>
            )}
          </nav>
        </>
      )}
      <SubscriptionBanner />
    </header>
  );
};

export const SourceNote = ({ className = "" }) => (
  <p className={`text-xs text-slate-500 ${className}`} data-testid="fsanz-source-note">
    Source: Food Standards Australia New Zealand. Food Recall Alerts is an independent alert service and is not
    FSANZ or an Australian Government service.
  </p>
);
